import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LanguageCode, Profile, validateVideoAd, VideoAd } from "../../models";
import { VideoAdForm } from "./VideoAdForm";
import { Button, LinearProgress, Modal } from "@mui/material";
import { getVideoAd, updateVideoAd } from "../../api";
import { useSnackbar } from "notistack";

interface VideoAdFormDialogProps {
  videoAdId: number;
  languageCodes: LanguageCode[];
  profiles: Profile[];
  onClose: () => void;
  onVideoAdSave: () => void;
}

export const VideoAdFormDialog: React.FC<VideoAdFormDialogProps> = ({ videoAdId, languageCodes, profiles, onClose, onVideoAdSave }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [videoAd, setVideoAd] = useState<Omit<VideoAd, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [error, setError] = useState<any | null>(null);

  // Form
  const [videoAdErrors, setVideoAdErrors] = useState<Record<keyof VideoAd, string>>();
  const [dirty, setDirty] = useState(false);

  const loading = useMemo(() => !videoAd && !error, [videoAd, error]);

  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);

    try {
      const videoAd = await getVideoAd(videoAdId);
      setVideoAd(videoAd);
    } catch (error) {
      setError(error);
    }
  }, [videoAdId]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function validate() {
    const errors = validateVideoAd(videoAd!);

    if (Object.keys(errors).length > 0) {
      setVideoAdErrors(errors as Record<keyof VideoAd, string>);
    } else {
      setVideoAdErrors(undefined);
    }

    return Object.keys(errors).length === 0;
  }

  function handleVideoAdChange(videoAd: Omit<VideoAd, "id" | "createdAt" | "updatedAt">) {
    if (dirty) {
      validate();
    }

    setVideoAd(videoAd);
  }

  async function handleVideoAdSave() {
    if (submitting) {
      return;
    }

    if (!validate()) {
      setDirty(true);
      return;
    }

    setDirty(false);

    enqueueSnackbar("Saving...", { variant: "info" });

    setSubmitting(true);

    try {
      await updateVideoAd(videoAdId, videoAd!);
      enqueueSnackbar("Video ad saved", { variant: "success" });
      await fetchData();
      onVideoAdSave();
    } catch (e) {
      enqueueSnackbar("Failed to save video ad", { variant: "error" });
      console.error(e);
    }

    setSubmitting(false);
  }

  return (
    <Modal open={true} onClose={onClose}>
      <div className="edit-item-container">
        {loading && <div style={{ width: "50%", margin: "auto" }}>
          <LinearProgress />
        </div>}
        {error && <div>Error: {error}</div>}
        {!loading && !error && <div className="social">
          <div className="box post-edit" style={{ margin: 0 }}>
            <VideoAdForm
              videoAd={videoAd!}
              onVideoAdChange={handleVideoAdChange}
              languageCodes={languageCodes}
              profiles={profiles}
              errors={videoAdErrors}
            >
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClose}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVideoAdSave}
                >
                  Save
                </Button>
                <div>Warning Content in modal maybe closer than they appear</div>
              </>
            </VideoAdForm>
          </div>
        </div>}
      </div>
    </Modal>
  );
};
